import React, { FC, useCallback, useMemo } from "react";
import { generatePath, useNavigate } from "react-router";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Layout } from "@lookiero/sty-psp-ui";
import { Product } from "../../../../domain/entities/product/Product";
import { ProductPreview } from "../../../../domain/projections/ProductPreview";
import { useAddProductToWishlist } from "../../behaviors/useAddProductToWishlist";
import { useGetBenefitMediaUrlByVariantId } from "../../behaviors/useGetBenefitMediaUrlByVariantId";
import { useGetExperiments } from "../../behaviors/useGetExperiments";
import { useGetOrder } from "../../behaviors/useGetOrder";
import { useMoveStockoutsToWishlist } from "../../behaviors/useMoveStockoutsToWishlist";
import { useRemoveProductFromBasket } from "../../behaviors/useRemoveProductFromBasket";
import { useRemoveVariantFromWishlist } from "../../behaviors/useRemoveVariantFromWishlist";
import { Basket as BasketTemplate } from "../../components/templates/basket/Basket";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { useToast } from "../../hooks/useToast";
import { DOMAIN, I18nMessages } from "../../i18n/i18n";
import { Routes } from "../../routing/routes";
import { List, TrackingPage, TrackingView } from "../../tracking/tracking";
import { useTrackBasketDisplayed } from "../../tracking/useTrackBasketDisplayed";
import { useTrackBasketItemRemoved } from "../../tracking/useTrackBasketItemRemoved";
import { useTrackItemSelected } from "../../tracking/useTrackItemSelected";
import { useTrackPageView } from "../../tracking/useTrackPageView";
import { useTrackWhishlistItemAdded } from "../../tracking/useTrackWhishlistItemAdded";
import { useTrackWhishlistItemRemoved } from "../../tracking/useTrackWhishlistItemRemoved";

interface BasketProps {
  readonly layout: Layout;
}

const Basket: FC<BasketProps> = ({ layout }) => {
  const {
    session: { user },
    basePath,
    tradename,
  } = useStaticInfo();
  const navigate = useNavigate();
  const toast = useToast();

  const stockOutNotificationRemovedText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.STOCK_OUT_NOTIFICATION_REMOVED,
  });
  const whishlistNotificationItemAddedText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.WISHLIST_NOTIFICATION_ITEM_ADDED,
  });
  const whishlistNotificationItemRemovedText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.WISHLIST_NOTIFICATION_ITEM_REMOVED,
  });

  const { order, isLoading: isLoadingOrder, refetchOrder } = useGetOrder();
  const { experiments } = useGetExperiments();

  const trackingCommonParams = useMemo(
    () => ({
      country: user.country,
      segment: user.segment,
      userId: user.id,
      tradename,
    }),
    [tradename, user.country, user.id, user.segment],
  );
  useTrackPageView({
    ...trackingCommonParams,
    page: TrackingPage.BASKET,
    view: TrackingView.LOOK_IN_BASKET,
  });
  useTrackBasketDisplayed({
    ...trackingCommonParams,
    experiments,
    order,
  });
  const trackBasketItemRemoved = useTrackBasketItemRemoved(trackingCommonParams);
  const trackItemSelected = useTrackItemSelected(trackingCommonParams);
  const trackWhishlistItemAdded = useTrackWhishlistItemAdded(trackingCommonParams);
  const trackWhishlistItemRemoved = useTrackWhishlistItemRemoved(trackingCommonParams);

  const addProductToWishlist = useAddProductToWishlist();
  const removeVariantFromWishlist = useRemoveVariantFromWishlist();
  const removeProductFromBasket = useRemoveProductFromBasket();

  const firstOrderItemVariantId = order.items?.[0]?.variantId || "";
  const benefitsMediaUrl = useGetBenefitMediaUrlByVariantId(firstOrderItemVariantId);

  const onMovedStockOutsToWishlist = useCallback(() => {
    toast.showSuccess({ message: stockOutNotificationRemovedText });
  }, [toast, stockOutNotificationRemovedText]);

  const moveStockoutsToWihslist = useMoveStockoutsToWishlist(onMovedStockOutsToWishlist);

  const moveStockOutsWishlistAndGoToCheckout = useCallback(() => {
    moveStockoutsToWihslist();
    navigate(`${basePath}/${Routes.CHECKOUT}`);
  }, [basePath, moveStockoutsToWihslist, navigate]);

  const handleOnRemoveProduct = useCallback(
    async (product: Product) => {
      const { orderId, serviceId } = order;
      const itemId = product.itemId || ""; // TODO: product without itemId??
      removeProductFromBasket(
        { itemId, orderId, serviceId },
        () => {
          refetchOrder();

          trackBasketItemRemoved({
            product,
            currency: order.currency,
          });
        },
        (error) => console.error(error),
      );
    },
    [order, removeProductFromBasket, refetchOrder, trackBasketItemRemoved],
  );

  const handleOnAddToWishlist = useCallback(
    (product: Product) => {
      const { variantId } = product;

      addProductToWishlist(
        variantId,
        () => {
          toast.showSuccess({ message: whishlistNotificationItemAddedText });

          refetchOrder();

          trackWhishlistItemAdded({
            product,
          });
        },
        (error) => console.error(error),
      );
    },
    [addProductToWishlist, toast, whishlistNotificationItemAddedText, refetchOrder, trackWhishlistItemAdded],
  );

  const handleOnRemoveFromWishlist = useCallback(
    (product: Product) => {
      const { variantId } = product;

      removeVariantFromWishlist(
        variantId,
        () => {
          toast.showSuccess({ message: whishlistNotificationItemRemovedText });

          refetchOrder();

          trackWhishlistItemRemoved({
            product,
          });
        },
        (error) => console.error(error),
      );
    },
    [removeVariantFromWishlist, toast, whishlistNotificationItemRemovedText, refetchOrder, trackWhishlistItemRemoved],
  );

  const handleOnPressImage = useCallback(
    (product: Product) => {
      const item = {
        id: product.variantId,
        title: product.title,
        brand: product.brand,
        price: product.price,
      } as ProductPreview;

      trackItemSelected({
        list: List.BASKET,
        productPreview: item,
      });

      navigate(generatePath(`${basePath}/${Routes.PRODUCT_DETAIL}`, { variantId: product.variantId }), {
        state: { innerNavigation: true },
      });
    },
    [basePath, navigate, trackItemSelected],
  );

  const handleOnBack = useCallback(() => navigate(-1), [navigate]);

  const handleOnPressWishlist = useCallback(() => navigate(`${basePath}/${Routes.WISHLIST}`), [basePath, navigate]);

  return (
    <BasketTemplate
      benefitsImageUrl={benefitsMediaUrl || ""}
      goToCheckout={moveStockOutsWishlistAndGoToCheckout}
      isLoading={isLoadingOrder}
      layout={layout}
      order={order}
      onAddToWishlist={handleOnAddToWishlist}
      onBack={handleOnBack}
      onPressImage={handleOnPressImage}
      onPressWishlist={handleOnPressWishlist}
      onRemoveFromWishlist={handleOnRemoveFromWishlist}
      onRemoveProduct={handleOnRemoveProduct}
    />
  );
};

export { Basket };
